import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import Sparkline from "./Sparkline";
import type { AgentMetrics, MetricsHistoryEntry } from "./livekit-types";

interface MetricsPanelProps {
    darkMode?: boolean;
    agentMetrics: AgentMetrics;
    metricsHistory: MetricsHistoryEntry[];
    showMetrics: boolean;
    setShowMetrics: (show: boolean) => void;
    /** When provided, the toggle is anchored to the bottom-left of this container instead of the viewport */
    containerRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Draggable metrics panel with sparkline charts for Overall, LLM, and TTS latency.
 * Uses percentage-based positioning for responsive behavior.
 */
const MetricsPanel: React.FC<MetricsPanelProps> = ({
    darkMode,
    agentMetrics,
    metricsHistory,
    showMetrics,
    setShowMetrics,
    containerRef,
}) => {
    // Shared hover state for all sparklines
    const [hoveredMetricIndex, setHoveredMetricIndex] = useState<number | null>(null);

    // Metrics panel position (draggable) — using percentage-based coordinates
    const [metricsPositionPercent, setMetricsPositionPercent] = useState({ x: 50, y: 40 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Convert percentage to pixels for rendering
    const metricsPosition = useMemo(
        () => ({
            x: (metricsPositionPercent.x / 100) * window.innerWidth,
            y: (metricsPositionPercent.y / 100) * window.innerHeight,
        }),
        [metricsPositionPercent]
    );

    // Handle window resize to keep position relative
    useEffect(() => {
        const handleResize = () => {
            setMetricsPositionPercent((prev) => ({ ...prev }));
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Handle dragging at window level to prevent issues when mouse leaves panel
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            const percentX = Math.max(0, Math.min((newX / window.innerWidth) * 100, 100 - (280 / window.innerWidth) * 100));
            const percentY = Math.max(0, Math.min((newY / window.innerHeight) * 100, 100 - (200 / window.innerHeight) * 100));
            setMetricsPositionPercent({ x: percentX, y: percentY });
        };

        const handleMouseUp = () => setIsDragging(false);

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    // Memoize sparkline data
    const overallData = useMemo(() => metricsHistory.map((m) => m.overall || 0), [metricsHistory]);
    const llmData = useMemo(() => metricsHistory.map((m) => m.llm || 0), [metricsHistory]);
    const ttsData = useMemo(() => metricsHistory.map((m) => m.tts || 0), [metricsHistory]);

    const isAnchoredToContainer = Boolean(containerRef?.current);

    const toggleButton = (
        <button
            onClick={() => setShowMetrics(!showMetrics)}
            className="metrics-toggle-icon"
            style={{
                position: isAnchoredToContainer ? "absolute" : "fixed",
                bottom: isAnchoredToContainer ? "56px" : "20px",
                left: "20px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingBottom: "1px",
                marginBottom: "3px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                zIndex: 10000,
                transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"; }}
            title={showMetrics ? "Hide metrics" : "Show metrics"}
        >
            i
        </button>
    );

    const content = (
        <>
            {/* Metrics Toggle Icon — Bottom Left of agent container or viewport */}
            {containerRef?.current
                ? createPortal(toggleButton, containerRef.current)
                : createPortal(toggleButton, document.body)}

            {/* Metrics Panel — Draggable */}
            {showMetrics && (
                <div
                    className="metrics-panel"
                    style={{
                        position: "fixed",
                        top: `${metricsPosition.y}px`,
                        left: `${metricsPosition.x}px`,
                        background: darkMode ? "#000" : "#282A36",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        borderRadius: "8px",
                        padding: "0",
                        minWidth: "280px",
                        maxHeight: "calc(100vh - 120px)",
                        overflowY: "auto",
                        zIndex: 10000,
                        fontSize: "13px",
                        color: "rgba(255, 255, 255, 0.9)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                    }}
                >
                    {/* Drag Handle Bar */}
                    <div
                        className="drag-handle"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(true);
                            const rect = (e.currentTarget.closest(".metrics-panel") as HTMLElement)?.getBoundingClientRect();
                            if (rect) setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                        }}
                        style={{
                            width: "100%",
                            padding: "8px 14px",
                            cursor: isDragging ? "grabbing" : "grab",
                            userSelect: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                            background: "rgba(255, 255, 255, 0.05)",
                            transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => { if (!isDragging) e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"; }}
                        onMouseLeave={(e) => { if (!isDragging) e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"; }}
                    >
                        {/* Grip Dots */}
                        <div style={{ display: "flex", gap: "4px", alignItems: "center", opacity: 0.6 }}>
                            {[0, 1, 2].map((i) => (
                                <div key={i} style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(255, 255, 255, 0.6)" }} />
                            ))}
                        </div>

                        {/* Hover Metrics Display */}
                        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", fontSize: "11px" }}>
                            {hoveredMetricIndex !== null && metricsHistory[hoveredMetricIndex] ? (
                                <>
                                    <span style={{ opacity: 0.6, fontSize: "10px" }}>
                                        {new Date(metricsHistory[hoveredMetricIndex].timestamp).toLocaleTimeString()}
                                    </span>
                                    {metricsHistory[hoveredMetricIndex].overall && (
                                        <span><span style={{ color: "#10b981" }}>●</span> <span style={{ fontWeight: 600 }}>{metricsHistory[hoveredMetricIndex].overall}ms</span></span>
                                    )}
                                    {metricsHistory[hoveredMetricIndex].llm && (
                                        <span><span style={{ color: "#60a5fa" }}>●</span> <span style={{ fontWeight: 600 }}>{metricsHistory[hoveredMetricIndex].llm}ms</span></span>
                                    )}
                                    {metricsHistory[hoveredMetricIndex].tts && (
                                        <span><span style={{ color: "#a78bfa" }}>●</span> <span style={{ fontWeight: 600 }}>{metricsHistory[hoveredMetricIndex].tts}ms</span></span>
                                    )}
                                </>
                            ) : null}
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={() => setShowMetrics(false)}
                            style={{
                                background: "none",
                                border: "none",
                                color: "rgba(255, 255, 255, 0.7)",
                                cursor: "pointer",
                                padding: "4px 8px",
                                fontSize: "18px",
                                borderRadius: "4px",
                                transition: "all 0.2s",
                                lineHeight: "1",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)"; e.currentTarget.style.color = "rgba(255, 255, 255, 1)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "rgba(255, 255, 255, 0.7)"; }}
                            title="Hide metrics"
                        >
                            ×
                        </button>
                    </div>

                    {/* Metrics Content */}
                    <div style={{ padding: "12px 14px", width: "100%", boxSizing: "border-box" }}>
                        {/* Overall Latency */}
                        <div style={{ marginBottom: "8px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
                                <span style={{ opacity: 0.8, fontWeight: 600 }}>Overall</span>
                                <span style={{ fontWeight: 700, color: "#10b981", fontSize: "15px" }}>
                                    {agentMetrics.overallLatency ? `${agentMetrics.overallLatency}ms` : "—"}
                                </span>
                            </div>
                            <Sparkline data={overallData} width={248} height={20} color="#10b981" fillColor="rgba(16, 185, 129, 0.15)" hoveredIndex={hoveredMetricIndex} onHoverChange={setHoveredMetricIndex} showTooltip={false} />
                        </div>

                        <div style={{ height: "1px", background: "rgba(255, 255, 255, 0.1)", margin: "8px 0" }} />

                        {/* LLM Latency */}
                        <div style={{ marginBottom: "8px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
                                <span style={{ opacity: 0.7, fontSize: "12px" }}>LLM Response</span>
                                <span style={{ fontWeight: 600, color: "#60a5fa" }}>
                                    {agentMetrics.llmLatency ? `${agentMetrics.llmLatency}ms` : "—"}
                                </span>
                            </div>
                            <Sparkline data={llmData} width={248} height={18} color="#60a5fa" fillColor="rgba(96, 165, 250, 0.15)" hoveredIndex={hoveredMetricIndex} onHoverChange={setHoveredMetricIndex} showTooltip={false} />
                        </div>

                        {/* TTS Latency */}
                        <div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "3px" }}>
                                <span style={{ opacity: 0.7, fontSize: "12px" }}>Speech Synthesis</span>
                                <span style={{ fontWeight: 600, color: "#a78bfa" }}>
                                    {agentMetrics.ttsLatency ? `${agentMetrics.ttsLatency}ms` : "—"}
                                </span>
                            </div>
                            <Sparkline data={ttsData} width={248} height={18} color="#a78bfa" fillColor="rgba(167, 139, 250, 0.15)" hoveredIndex={hoveredMetricIndex} onHoverChange={setHoveredMetricIndex} showTooltip={false} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    return createPortal(content, document.body);
};

export default MetricsPanel;
